import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone, Globe, MessageCircle } from "lucide-react";
import { useLang } from "../i18n";
import { Header } from "../components/Header";

const RESOURCES = {
  global: [
    { name: "Befrienders Worldwide", contact: "befrienders.org", note: "Emotional support & suicide prevention worldwide" },
    { name: "International Hotline Directory", contact: "findahelpline.com", note: "Free, confidential support in 130+ countries" },
    { name: "UN Women — Ending Violence", contact: "unwomen.org", note: "Global directory of national helplines" },
    { name: "HotPeachPages", contact: "hotpeachpages.net", note: "Abuse hotlines & shelters in 110 languages" },
  ],
  ru: [
    { name: "Всероссийский телефон доверия", contact: "8-800-7000-600", note: "Для женщин, переживших насилие · бесплатно, анонимно" },
    { name: "Центр «Насилию.нет»", contact: "nasiliu.net", note: "Психологическая и юридическая помощь" },
    { name: "Кризисный центр «АННА»", contact: "8-800-7000-600", note: "Помощь пострадавшим от домашнего насилия" },
    { name: "Единый телефон экстренных служб", contact: "112", note: "При непосредственной опасности" },
  ],
};

function Card({ item, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: i * 0.08 }}
      data-testid="resource-card"
      className="flex items-start gap-4 rounded-panel bg-white p-5 shadow-soft"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-softpink text-dusty">
        <MessageCircle size={18} strokeWidth={1.5} />
      </span>
      <div>
        <h3 className="text-base font-semibold text-graphite">{item.name}</h3>
        <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-dusty-dark">
          <Phone size={14} strokeWidth={1.75} />
          {item.contact}
        </div>
        <p className="mt-1.5 text-sm leading-relaxed text-textsec">{item.note}</p>
      </div>
    </motion.div>
  );
}

export default function Resources() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-softpink">
      <Header />
      <div
        className="relative flex min-h-[42vh] items-end px-5 pb-10 pt-28 sm:px-8"
        style={{
          backgroundImage:
            "linear-gradient(to bottom, rgba(255,255,255,0.3), rgba(250,243,244,0.95)), url(https://images.unsplash.com/photo-1474540412665-1cdae210ae6b?crop=entropy&cs=srgb&fm=jpg&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="mx-auto w-full max-w-4xl">
          <Link to="/map" data-testid="resources-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-graphite hover:text-dusty-dark">
            <ArrowLeft size={16} strokeWidth={1.75} />
            {t("res_back")}
          </Link>
          <h1 className="mt-5 text-4xl font-semibold tracking-tight text-graphite sm:text-5xl">{t("res_title")}</h1>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-textsec">{t("res_sub")}</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 py-14 sm:px-8">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-dusty">
          <Globe size={16} strokeWidth={1.75} />
          {t("res_global")}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {RESOURCES.global.map((r, i) => (
            <Card key={r.name} item={r} i={i} />
          ))}
        </div>

        <div className="mb-4 mt-12 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-dusty">
          <Globe size={16} strokeWidth={1.75} />
          {t("res_ru")}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {RESOURCES.ru.map((r, i) => (
            <Card key={r.name} item={r} i={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
